import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import "./App.css";
import "./styles/globals.css";
import "./styles/retro-theme.css";
import "./styles/cyberpunk-theme.css";
import "./styles/damaged-theme.css";
import "./ModernApp.css";
import './i18n'; // 初始化i18n

// 新的核心模块
import { dataManager, CALCULATION_TYPE } from './core/DataManager';
import { generateStatisticsReport } from './core/ReportGenerator';

// 新的UI组件
import Modal from './components/ui/Modal';
import LanguageSelector from './components/ui/LanguageSelector';
import ThemeSelector from './components/ThemeSelector';
import useTheme from './hooks/useTheme';

// 新的表单组件
import OvertimeForm from './components/forms/OvertimeForm';
import RestForm from './components/forms/RestForm';



// 保留的旧组件（临时）
import RecordList from "./components/RecordList";
import Toast from "./components/Toast";


function App() {
  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  // 主题管理 - 主题状态由useTheme hook自动管理
  useTheme();

  // 新的数据管理方式
  const [appData, setAppData] = useState(dataManager.getAllData());
  const [statistics, setStatistics] = useState(null);

  // UI状态
  const [activeModal, setActiveModal] = useState(null);
  const [tempAnnualLeaveDays, setTempAnnualLeaveDays] = useState(0);
  const [tempCustomHours, setTempCustomHours] = useState(''); // 自定义小时数

  // 兼容性：保留旧的状态变量名（从新数据中获取）
  const overtimeRecords = appData.overtimeRecords;
  const consumptionRecords = appData.consumptionRecords;
  const restRecords = appData.restRecords;
  const annualLeave = appData.annualLeave;
  const annualLeaveRecords = appData.annualLeaveRecords;

  // 数据管理器监听器
  useEffect(() => {
    const handleDataChange = (changeType, _data, allData) => {
      setAppData(allData);
      updateStatistics(allData);

      // 显示操作成功提示
      const messages = {
        'OVERTIME_ADDED': t('messages.overtimeAdded', '加班记录已添加'),
        'REST_ADDED': t('messages.restAdded', '调休记录已添加'),
        'OVERTIME_DELETED': t('messages.overtimeDeleted', '加班记录已删除'),
        'REST_DELETED': t('messages.restDeleted', '调休记录已删除'),
        'DATA_SAVED': t('messages.dataSaved', '数据已保存'),
        'DATA_LOADED': t('messages.dataLoaded', '数据已加载'),
        'DATA_IMPORTED': t('messages.dataImported', '数据已导入'),
        'DATA_EXPORTED': t('messages.dataExported', '数据已导出')
      };

      if (messages[changeType]) {
        showToast(messages[changeType]);
      }
    };

    dataManager.addListener(handleDataChange);

    // 尝试从本地存储加载数据
    try {
      const loaded = dataManager.loadFromLocalStorage();
      if (loaded) {
        console.log('数据加载成功');
      }
    } catch (error) {
      console.log('没有找到本地数据，使用默认数据:', error.message);
    }

    // 启动时清理无效数据
    setTimeout(() => {
      const cleanupCount = dataManager.cleanupInvalidData();
      if (cleanupCount > 0) {
        console.log(`Cleaned up ${cleanupCount} invalid records on startup`);
        showToast(t('messages.cleanupComplete', { count: cleanupCount }));
      }
    }, 1000);

    return () => {
      dataManager.removeListener(handleDataChange);
    };
  }, [t]);

  // 更新统计信息
  const updateStatistics = (data) => {
    const stats = generateStatisticsReport(
      data.overtimeRecords,
      data.restRecords,
      data.consumptionRecords,
      data.annualLeave
    );
    setStatistics(stats);
  };

  // 保留的UI状态（兼容性）
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);


  const [toasts, setToasts] = useState([]);

  const showToast = (message) => {
    // 使用更可靠的ID生成方法，避免同一毫秒内的重复
    const id = Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);
    setToasts(prevToasts => [...prevToasts, { id, message }]);
  };

  const removeToast = (id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };

  useEffect(() => {
    localStorage.setItem("overtimeRecords", JSON.stringify(overtimeRecords));
  }, [overtimeRecords]);

  useEffect(() => {
    localStorage.setItem(
      "consumptionRecords",
      JSON.stringify(consumptionRecords)
    );
  }, [consumptionRecords]);

  useEffect(() => {
    localStorage.setItem("restRecords", JSON.stringify(restRecords));
  }, [restRecords]);

  // 新增年假相关的 useEffect
  useEffect(() => {
    localStorage.setItem("annualLeave", JSON.stringify(annualLeave));
  }, [annualLeave]);

  useEffect(() => {
    localStorage.setItem("annualLeaveRecords", JSON.stringify(annualLeaveRecords));
  }, [annualLeaveRecords]);

  // 新的处理函数（使用数据管理器）
  const handleOvertimeRecord = async (record) => {
    try {
      dataManager.addOvertimeRecord(record.startTime, record.endTime);
      if (activeModal === 'overtime') {
        setActiveModal(null);
      }
    } catch (error) {
      showToast(error.message);
    } finally {
      // Loading state removed
    }
  };

  const handleRestRecord = async (record) => {
    try {
      dataManager.addRestRecord(record.txbegintime, record.txendtime);
      if (activeModal === 'rest') {
        setActiveModal(null);
      }
    } catch (error) {
      showToast(error.message);
    } finally {
      // Loading state removed
    }
  };



  // 模态框管理
  const openModal = (modalType) => {
    setActiveModal(modalType);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  // 新的处理函数（使用数据管理器）
  const handleSetAnnualLeave = (days) => {
    try {
      dataManager.setAnnualLeaveDays(days);
      closeModal(); // 设置成功后关闭模态框
    } catch (error) {
      showToast(error.message);
    }
  };

  // 年假设置相关函数
  const handleAnnualLeaveOptionClick = (days) => {
    setTempAnnualLeaveDays(days);
    setTempCustomHours(''); // 清除自定义小时数
  };

  const handleConfirmAnnualLeave = () => {
    // 优先使用自定义小时数
    if (tempCustomHours !== '') {
      const hours = parseInt(tempCustomHours);
      if (!isNaN(hours) && hours >= 0) {
        try {
          dataManager.setAnnualLeaveHours(hours);
          closeModal();
        } catch (error) {
          showToast(error.message);
        }
      } else {
        showToast(t('messages.invalidHours', '请输入有效的小时数'));
      }
    } else {
      handleSetAnnualLeave(tempAnnualLeaveDays);
    }
  };

  const openAnnualLeaveModal = () => {
    setTempAnnualLeaveDays(appData.annualLeave.totalDays || 0);
    setTempCustomHours(''); // 重置自定义小时数
    setActiveModal('annualLeave');
  };

  // 数据操作

  const handleExportData = () => {
    try {
      dataManager.exportToFile('v2');
    } catch (error) {
      showToast(error.message);
    }
  };

  const handleImportData = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await dataManager.importFromFile(file);
      } catch (error) {
        showToast(error.message);
      }
    }
  };



  // 兼容性处理函数（简化版）
  const handleExpand = (record) => {
    setExpandedRecord(record);
  };

  const handleCollapse = () => {
    setExpandedRecord(null);
  };

  const handleDelete = (record) => {
    if (window.confirm(t('confirmDialog.deleteMessage', '确定要删除这条记录吗？'))) {
      try {
        dataManager.deleteOvertimeRecord(record.id);
      } catch (error) {
        showToast(error.message);
      }
    }
  };

  const handleEdit = (record) => {
    setEditRecord(record);
    setShowEditModal(true);
  };

  const handleEditSubmit = (updatedRecord) => {
    try {
      if (updatedRecord.txbegintime) {
        // 编辑调休记录
        dataManager.updateRestRecord(updatedRecord.id, updatedRecord);
        showToast(t('messages.restRecordUpdated', '调休记录更新成功！'));
      } else if (updatedRecord.type === "年假") {
        // 编辑年假记录
        dataManager.updateAnnualLeaveRecord(updatedRecord.id, updatedRecord);
        showToast(t('messages.annualLeaveRecordUpdated', '年假记录更新成功！'));
      } else {
        // 编辑加班记录
        dataManager.updateOvertimeRecord(updatedRecord.id, updatedRecord);
        showToast(t('messages.overtimeRecordUpdated', '加班记录更新成功！'));
      }
      setShowEditModal(false);
    } catch (error) {
      showToast(error.message);
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
  };



  // 计算可用时间
  const availableHours = {
    overtime: statistics?.overtime.remainingHours || 0,
    annualLeave: statistics?.annualLeave.remainingHours || 0
  };

  // 简化的删除处理函数
  const handleRestDelete = (record) => {
    if (window.confirm(t('confirmDialog.deleteRestMessage', '确定要删除这条摆烂记录？'))) {
      try {
        dataManager.deleteRestRecord(record.id);
      } catch (error) {
        showToast(error.message);
      }
    }
  };

  // 简化的编辑处理函数
  const handleRestEdit = (record) => {
    setEditRecord(record);
    setShowEditModal(true);
  };

  const handleRestEditSubmit = (updatedRecord) => {
    try {
      dataManager.updateRestRecord(updatedRecord.id, updatedRecord);
      showToast(t('messages.restRecordUpdated', '调休记录更新成功！'));
      setShowEditModal(false);
    } catch (error) {
      showToast(error.message);
    }
  };

  // 简化的年假处理函数
  const handleAnnualLeaveEdit = (record) => {
    setEditRecord(record);
    setShowEditModal(true);
  };

  const handleAnnualLeaveDelete = (record) => {
    if (window.confirm(t('confirmDialog.deleteAnnualLeaveMessage', '确定要删除这条年假记录吗？'))) {
      try {
        dataManager.deleteAnnualLeaveRecord(record.id);
      } catch (error) {
        showToast(error.message);
      }
    }
  };



  return (
    <div className="modern-app">
      {/* 现代化头部 */}
      <header className="modern-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo-section">
              <div className="logo-icon">🐂</div>
              <div className="logo-text">
                <h1>{t('app.title', '牛马摆烂工具')}</h1>
                <span className="version-badge">V2.2</span>
              </div>
            </div>
          </div>

          <div className="header-right">
            <LanguageSelector size="sm" />
          </div>
        </div>
      </header>

      {/* 现代化统计卡片 */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <div className="stat-value">{overtimeRecords.reduce((total, record) => total + record.duration, 0).toFixed(1)}h</div>
            <div className="stat-label">{t('stats.totalOvertime', '累计加班时间')}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{overtimeRecords.reduce((total, record) => total + record.remainingTime, 0).toFixed(1)}h</div>
            <div className="stat-label">{t('stats.availableOvertime', '可用加班时间')}</div>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">🏖️</div>
          <div className="stat-content">
            <div className="stat-value">{annualLeave.remainingHours.toFixed(1)}h</div>
            <div className="stat-label">{t('stats.remainingAnnualLeave', '剩余年假时间')}</div>
            <div className="stat-sub">({annualLeave.totalDays || 0} {t('common.days', '天')})</div>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">😴</div>
          <div className="stat-content">
            <div className="stat-value">{restRecords.reduce((total, record) => total + record.duration, 0).toFixed(1)}h</div>
            <div className="stat-label">{t('stats.totalRest', '已摆烂时间')}</div>
            <div className="stat-sub">{(restRecords.reduce((total, record) => total + record.duration, 0) / 8).toFixed(1)} {t('common.days', '天')}</div>
          </div>
        </div>
      </div>



      {/* 现代化操作区域 */}
      <div className="action-section">
        <div className="primary-actions">
          <button
            className="action-btn primary"
            onClick={() => openModal('overtime')}
          >
            <div className="btn-icon">⏱️</div>
            <div className="btn-content">
              <div className="btn-title">{t('actions.recordOvertime', '记录加班')}</div>
              <div className="btn-subtitle">{t('actions.addOvertimeTime', '添加加班时间')}</div>
            </div>
          </button>

          <button
            className="action-btn success"
            onClick={() => openModal('rest')}
            disabled={availableHours.overtime + availableHours.annualLeave <= 0}
          >
            <div className="btn-icon">😴</div>
            <div className="btn-content">
              <div className="btn-title">{t('actions.requestRest', '申请调休')}</div>
              <div className="btn-subtitle">{t('actions.useOvertimeTime', '使用加班时间')}</div>
            </div>
          </button>

          <button
            className="action-btn info"
            onClick={openAnnualLeaveModal}
          >
            <div className="btn-icon">🏖️</div>
            <div className="btn-content">
              <div className="btn-title">{t('actions.setAnnualLeave', '设置年假')}</div>
              <div className="btn-subtitle">{t('actions.configureAnnualLeaveDays', '配置年假天数')}</div>
            </div>
          </button>
        </div>

        <div className="secondary-actions">
          <button
            className="secondary-btn"
            onClick={() => openModal('annualLeaveRecord')}
            disabled={annualLeave.remainingHours <= 0}
          >
            <span className="btn-icon">🏖️</span>
            {t('actions.recordAnnualLeave', '记录年假')}
          </button>

          <button className="secondary-btn" onClick={handleExportData}>
            <span className="btn-icon">📤</span>
{t('actions.exportData', '导出数据')}
          </button>

          <input
            type="file"
            accept=".牛马"
            onChange={handleImportData}
            style={{ display: 'none' }}
            id="import-file"
          />
          <button
            className="secondary-btn"
            onClick={() => document.getElementById('import-file').click()}
          >
            <span className="btn-icon">📥</span>
{t('actions.importData', '导入数据')}
          </button>


        </div>
      </div>
      {/* 记录列表 */}
      <div className="records-section">
        <RecordList
          overtimeRecords={overtimeRecords}
          restRecords={restRecords}
          consumptionRecords={consumptionRecords}
          annualLeaveRecords={annualLeaveRecords}
          expandedRecord={expandedRecord}
          onExpand={handleExpand}
          onCollapse={handleCollapse}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onRestDelete={handleRestDelete}
          onRestEdit={handleRestEdit}
          showEditModal={showEditModal}
          editRecord={editRecord}
          setEditRecord={setEditRecord}
          onEditSubmit={handleEditSubmit}
          onRestEditSubmit={handleRestEditSubmit}
          onEditCancel={handleEditCancel}
          onAnnualLeaveDelete={handleAnnualLeaveDelete}
          onAnnualLeaveEdit={handleAnnualLeaveEdit}
          showToast={showToast}
        />
      </div>

      {/* 模态框 */}
      {activeModal === 'overtime' && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={t('modals.recordOvertimeTitle', '记录加班')}
          size="lg"
        >
          <OvertimeForm
            onSubmit={handleOvertimeRecord}
            onCancel={closeModal}
          />
        </Modal>
      )}

      {activeModal === 'rest' && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={t('modals.requestRestTitle', '申请调休')}
          size="lg"
        >
          <RestForm
            onSubmit={handleRestRecord}
            onCancel={closeModal}
            availableHours={availableHours}
          />
        </Modal>
      )}

      {activeModal === 'annualLeave' && (
        <Modal
          open={true}
          onClose={closeModal}
          title={t('modals.setAnnualLeaveTitle', '🏖️ 设置年假天数')}
          size="md"
        >
          <div className="modern-annual-leave-form">
            <div className="current-status">
              <div className="status-item">
                <span className="status-label">{t('forms.currentAnnualLeave', '当前年假：')}</span>
                <span className="status-value">{appData.annualLeave.totalDays || 0} {t('common.days', '天')}</span>
              </div>
              <div className="status-item">
                <span className="status-label">{t('forms.remainingTime', '剩余时间：')}</span>
                <span className="status-value">{appData.annualLeave.remainingHours.toFixed(1)} {t('time.hours', '小时')}</span>
              </div>
            </div>

            <div className="annual-leave-options">
              {[0, 5, 10, 15].map(days => (
                <button
                  key={days}
                  className={`option-btn ${tempAnnualLeaveDays === days ? 'active' : ''}`}
                  onClick={() => handleAnnualLeaveOptionClick(days)}
                >
                  <div className="option-icon">
                    {days === 0 ? '❌' : days === 5 ? '🌟' : days === 10 ? '⭐' : '🎯'}
                  </div>
                  <div className="option-text">
                    {days === 0 ? t('forms.noAnnualLeave', '无年假') : `${days} ${t('common.days', '天')}`}
                  </div>
                  <div className="option-hours">
                    {days === 0 ? `0 ${t('time.hours', '小时')}` : `${days * 8} ${t('time.hours', '小时')}`}
                  </div>
                </button>
              ))}
            </div>

            <div className="custom-input-section">
              <label className="custom-label">{t('forms.customHours', '自定义小时：')}</label>
              <input
                type="number"
                min="0"
                max="240"
                placeholder={t('forms.enterHours', '输入小时')}
                className="custom-input"
                value={tempCustomHours}
                onChange={(e) => {
                  setTempCustomHours(e.target.value);
                  // 输入自定义小时时，清除按天选择的状态
                  if (e.target.value !== '') {
                    setTempAnnualLeaveDays(-1); // 用-1表示没有选择按天
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmAnnualLeave();
                  }
                }}
              />
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeModal}>
                {t('common.cancel', '取消')}
              </button>
              <button className="submit-btn" onClick={handleConfirmAnnualLeave}>
                {t('common.confirm', '确认')}
              </button>
            </div>
          </div>
        </Modal>
      )}



      {/* 加班记录模态框 */}
      {activeModal === 'overtime' && (
        <Modal
          open={true}
          onClose={closeModal}
          title={t('modals.recordOvertimeTimeTitle', '⏱️ 记录加班时间')}
          size="md"
        >
          <div className="modern-form">
            <div className="form-group">
              <label className="form-label">{t('time.startTime', '开始时间：')}</label>
              <input
                type="datetime-local"
                className="form-input"
                id="overtime-start"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('time.endTime', '结束时间：')}</label>
              <input
                type="datetime-local"
                className="form-input"
                id="overtime-end"
              />
            </div>

            <div className="form-actions">
              <button className="cancel-btn" onClick={closeModal}>
                {t('common.cancel', '取消')}
              </button>
              <button
                className="submit-btn"
                onClick={() => {
                  const startInput = document.getElementById('overtime-start');
                  const endInput = document.getElementById('overtime-end');

                  if (startInput.value && endInput.value) {
                    try {
                      dataManager.addOvertimeRecord(
                        new Date(startInput.value),
                        new Date(endInput.value)
                      );
                      closeModal();
                      showToast(t('messages.overtimeRecordAdded', '加班记录添加成功！'));
                    } catch (error) {
                      showToast(error.message);
                    }
                  } else {
                    showToast(t('messages.pleaseCompleteTimeInfo', '请填写完整的时间信息'));
                  }
                }}
              >
                {t('common.confirmAdd', '确认添加')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* 调休申请模态框 */}
      {activeModal === 'rest' && (
        <Modal
          open={true}
          onClose={closeModal}
          title={t('modals.requestRestTimeTitle', '😴 申请调休时间')}
          size="md"
        >
          <div className="modern-form">
            <div className="form-group">
              <label className="form-label">{t('time.startTime', '开始时间：')}</label>
              <input
                type="datetime-local"
                className="form-input"
                id="rest-start"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('time.endTime', '结束时间：')}</label>
              <input
                type="datetime-local"
                className="form-input"
                id="rest-end"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('forms.calculationType', '计算方式：')}</label>
              <select
                className="form-input"
                id="rest-calculation-type"
                defaultValue={CALCULATION_TYPE.WORKING_HOURS}
              >
                <option value={CALCULATION_TYPE.WORKING_HOURS}>
                  {t('forms.workingHours', '按工作时间计算（每天最多8小时，扣除午休）')}
                </option>
                <option value={CALCULATION_TYPE.ACTUAL_HOURS}>
                  {t('forms.actualHours', '按实际时间计算（起止时间差）')}
                </option>
              </select>
            </div>

            <div className="form-actions">
              <button className="cancel-btn" onClick={closeModal}>
                {t('common.cancel', '取消')}
              </button>
              <button
                className="submit-btn"
                onClick={() => {
                  const startInput = document.getElementById('rest-start');
                  const endInput = document.getElementById('rest-end');
                  const calculationTypeInput = document.getElementById('rest-calculation-type');

                  if (startInput.value && endInput.value) {
                    try {
                      dataManager.addRestRecord(
                        new Date(startInput.value),
                        new Date(endInput.value),
                        calculationTypeInput.value
                      );
                      closeModal();
                      showToast(t('messages.restRequestSubmitted', '调休申请提交成功！'));
                    } catch (error) {
                      showToast(error.message);
                    }
                  } else {
                    showToast(t('messages.pleaseCompleteTimeInfo', '请填写完整的时间信息'));
                  }
                }}
              >
                {t('common.confirmRequest', '确认申请')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* 年假记录模态框 */}
      {activeModal === 'annualLeaveRecord' && (
        <Modal
          open={true}
          onClose={closeModal}
          title={t('modals.recordAnnualLeaveTitle', '🏖️ 记录年假时间')}
          size="md"
        >
          <div className="modern-form">
            <div className="form-group">
              <label className="form-label">{t('forms.annualLeaveStartTime', '年假开始时间:')}</label>
              <input
                type="datetime-local"
                id="annual-leave-start"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('forms.annualLeaveEndTime', '年假结束时间:')}</label>
              <input
                type="datetime-local"
                id="annual-leave-end"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('forms.calculationType', '计算方式：')}</label>
              <select
                className="form-input"
                id="annual-leave-calculation-type"
                defaultValue={CALCULATION_TYPE.WORKING_HOURS}
              >
                <option value={CALCULATION_TYPE.WORKING_HOURS}>
                  {t('forms.workingHours', '按工作时间计算（每天最多8小时，扣除午休）')}
                </option>
                <option value={CALCULATION_TYPE.ACTUAL_HOURS}>
                  {t('forms.actualHours', '按实际时间计算（起止时间差）')}
                </option>
              </select>
            </div>

            <div className="form-info">
              <p className="info-text">{t('forms.remainingTime', '剩余时间：')}{annualLeave.remainingHours.toFixed(1)} {t('time.hours', '小时')}</p>
            </div>

            <div className="form-actions">
              <button className="cancel-btn" onClick={closeModal}>
                {t('common.cancel', '取消')}
              </button>
              <button
                className="submit-btn"
                onClick={() => {
                  const startInput = document.getElementById('annual-leave-start');
                  const endInput = document.getElementById('annual-leave-end');
                  const calculationTypeInput = document.getElementById('annual-leave-calculation-type');

                  if (startInput.value && endInput.value) {
                    try {
                      dataManager.addAnnualLeaveRecord(
                        new Date(startInput.value),
                        new Date(endInput.value),
                        calculationTypeInput.value
                      );
                      closeModal();
                      showToast(t('messages.annualLeaveRecordAdded', '年假记录添加成功！'));
                    } catch (error) {
                      showToast(error.message);
                    }
                  } else {
                    showToast(t('messages.pleaseCompleteTimeInfo', '请填写完整的时间信息'));
                  }
                }}
              >
                {t('common.confirmAdd', '确认添加')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Toast通知 */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* 主题切换悬浮窗 */}
      <ThemeSelector />
    </div>
  );
}

export default App;
